import type { AppEvent } from "../types";
import { normalizeCourseKey } from "../../../shared/utils/normalizeCourse";
import { fetchDuSchedule } from "./duClient";

/**
 * DU JSON can differ.
 * We try to extract lessons from multiple possible fields.
 */
function extractLessons(duJson: any): any[] {
  if (!duJson) return [];
  if (Array.isArray(duJson)) return duJson;
  if (Array.isArray(duJson.body)) return duJson.body;
  if (Array.isArray(duJson.lessons)) return duJson.lessons;
  if (Array.isArray(duJson.items)) return duJson.items;
  return [];
}

function pickStr(v: any): string {
  if (v == null) return "";
  return String(v).trim();
}

function parseDateAny(v: any): Date | null {
  const s = pickStr(v);
  if (!s) return null;

  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return d;

  return null;
}

const DAY_TO_INDEX: Record<string, number> = { d1: 0, d2: 1, d3: 2, d4: 3, d5: 4, d6: 5 };

const SLOT_TO_TIME: Record<string, { start: string; end: string }> = {
  a: { start: "08:00", end: "08:50" },
  b: { start: "09:00", end: "09:50" },
  c: { start: "10:00", end: "10:50" },
  d: { start: "11:00", end: "11:50" },
  e: { start: "12:00", end: "12:50" },
  f: { start: "13:05", end: "13:55" },
  g: { start: "14:00", end: "14:50" },
  h: { start: "15:00", end: "15:50" },
  i: { start: "16:00", end: "16:50" },
  j: { start: "17:00", end: "17:50" },
  k: { start: "18:00", end: "18:50" },
  l: { start: "19:00", end: "19:50" },
  m: { start: "20:00", end: "20:50" },
  n: { start: "21:00", end: "21:50" },
};

function startOfWeekMondayLocal(now = new Date()) {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  const jsDay = d.getDay(); // 0=Sun .. 6=Sat
  const mondayOffset = (jsDay + 6) % 7;
  d.setDate(d.getDate() - mondayOffset);
  return d;
}

function setHHMM(base: Date, hhmm: string) {
  const [hh, mm] = hhmm.split(":").map((x) => parseInt(x, 10));
  const d = new Date(base);
  d.setHours(hh || 0, mm || 0, 0, 0);
  return d;
}

function djb2(str: string) {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h) ^ str.charCodeAt(i);
  return (h >>> 0).toString(16);
}

function buildUid(group: string, startIso: string, subject: string, room?: string, teacher?: string, lessonType?: string) {
  return `du:h${djb2(`${group}|${startIso}|${subject}|${room || ""}|${teacher || ""}|${lessonType || ""}`)}`;
}

/**
 * MAIN: Converts DU schedule into concrete events for CURRENT week.
 * This will be used by Calendar/Home pages.
 */
export async function loadGroupScheduleAsEvents(groupName: string): Promise<AppEvent[]> {
  const group = (groupName || "").trim();
  if (!group) return [];

  const duJson = await fetchDuSchedule(group);
  const lessons = extractLessons(duJson);

  const out: AppEvent[] = [];

  for (const l of lessons) {
    const subject =
      pickStr(l?.subjectName) ||
      pickStr(l?.subject) ||
      pickStr(l?.disciplineName) ||
      pickStr(l?.name) ||
      "Lesson";

    const room =
      pickStr(l?.room) ||
      pickStr(l?.auditorium) ||
      pickStr(l?.classroom) ||
      pickStr(l?.cabinet) ||
      "";

    const lessonType =
      pickStr(l?.lessonType) ||
      pickStr(l?.lesson_type) ||
      pickStr(l?.lessonTypeName) ||
      "";

    let start =
      parseDateAny(l?.startDateTime) ||
      parseDateAny(l?.start) ||
      parseDateAny(l?.startAt) ||
      parseDateAny(l?.beginDateTime);

    let end =
      parseDateAny(l?.endDateTime) ||
      parseDateAny(l?.end) ||
      parseDateAny(l?.endAt) ||
      parseDateAny(l?.finishDateTime);

    if (!start) {
      const dayCode = pickStr(l?.classtimeDay || l?.ClasstimeDay || l?.classtime_day).toLowerCase();
      const slotCode = pickStr(l?.classtimeTime || l?.ClasstimeTime || l?.classtime_time).toLowerCase();
      const dayIndex = DAY_TO_INDEX[dayCode];
      const slot = SLOT_TO_TIME[slotCode];

      if (dayIndex != null && slot) {
        const monday = startOfWeekMondayLocal(new Date());
        const dayBase = new Date(monday);
        dayBase.setDate(monday.getDate() + dayIndex);

        start = setHHMM(dayBase, slot.start);
        end = setHHMM(dayBase, slot.end);
      }
    }

    if (!start) continue;

    const endSafe =
      end && !Number.isNaN(end.getTime())
        ? end
        : new Date(start.getTime() + 90 * 60_000);

    const courseName = subject.trim();
    const courseKey = normalizeCourseKey(courseName);

    const teacher = pickStr(l?.tutor) || pickStr(l?.teacher) || undefined;

    out.push({
      uid: buildUid(group, start.toISOString(), courseName, room || undefined, teacher, lessonType || undefined),
      kind: "lesson",
      title: courseName,
      courseName,
      courseKey: courseKey || undefined,
      lessonType: lessonType || undefined,
      start: start.toISOString(),
      end: endSafe.toISOString(),
      room: room || undefined,
      teacher,
      moodleUrl: pickStr(l?.teamsMeetingUrl) || pickStr(l?.meetingUrl) || undefined,
      source: { provider: "group", rawId: group },
    });
  }

  out.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  return out;
}

/**
 * OPTIONAL: groups list/search
 * If your app already has groups list from your .NET API, keep old code.
 * If not, leave empty.
 */
export async function fetchAllGroups(): Promise<string[]> {
  return [];
}

export async function searchGroups(): Promise<string[]> {
  return [];
}
